import React, { useMemo } from 'react';
import {
  ExpanderProps,
  TabbedAccordion,
  TabbedAccordionProps,
  IconType,
  LinkTarget,
} from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import {
  AssociatedContent,
  Expander,
  MlAccordion,
  MlNavtab,
  ReusableContentSection,
  ReusableContentSectionContentLinksItem,
} from 'src/graphql/generated/graphql';

import SectionHeading from './SectionHeading';

interface TabsSectionProps {
  tabs: MlNavtab;
}

const getPropsForReusableContentSectionType = (
  reusableContentSection: ReusableContentSection,
  tabsLabels: string[],
  accordionIndex: number,
): ExpanderProps => {
  const contentSectionDescription = (
    <div css={contentSectionDescriptionStyles}>
      {reusableContentSection.contentSectionTitle.contentSectionSubtitleRichText?.json
        ? documentToReactComponents(reusableContentSection.contentSectionTitle.contentSectionSubtitleRichText.json)
        : reusableContentSection.contentSectionTitle.contentSectionSubtitle}
    </div>
  );

  return {
    title: tabsLabels[accordionIndex] ?? reusableContentSection.contentSectionTitle.contentSectionTitleExternal,
    description: contentSectionDescription,
    resources: reusableContentSection.contentLinksCollection.items.map(
      (link: ReusableContentSectionContentLinksItem) => {
        const associatedContent = link as AssociatedContent;

        return {
          description: associatedContent.description,
          link: {
            link: associatedContent.link?.link,
            target: associatedContent.link?.target as LinkTarget,
            label: associatedContent.link?.label,
            icon: (associatedContent.link?.icon as unknown as IconType) ?? '',
          },
          type: associatedContent.tag,
        };
      },
    ),
  };
};

const getPropsForExpanderType = (expander: Expander, tabsLabels: string[], accordionIndex: number): ExpanderProps => {
  const contentSectionDescription = expander.description ? (
    <div css={contentSectionDescriptionStyles}>{documentToReactComponents(expander.description?.json)}</div>
  ) : undefined;

  const expandedContentSection = expander.expandedContent ? (
    <div css={contentSectionDescriptionStyles}>{documentToReactComponents(expander.expandedContent?.json)}</div>
  ) : undefined;

  return {
    title: tabsLabels[accordionIndex] ?? expander.title,
    description: contentSectionDescription,
    expandedContent: expandedContentSection,
    resources: expander.resourcesCollection.items.map((link: ReusableContentSectionContentLinksItem) => {
      const associatedContent = link as AssociatedContent;

      return {
        description: associatedContent.description,
        link: {
          link: associatedContent.link?.link,
          target: associatedContent.link?.target as LinkTarget,
          label: associatedContent.link?.label,
          icon: (associatedContent.link?.icon as unknown as IconType) ?? '',
        },
        type: associatedContent.tag,
      };
    }),
  };
};

const TabsSection: React.FC<TabsSectionProps> = ({ tabs }) => {
  const tabData: TabbedAccordionProps['tabs'] = useMemo(
    () =>
      tabs.contentCollection.items.map((item, index) => {
        const accordion = item as MlAccordion;
        const iconType = accordion.iconType as IconType;
        const tabsLabels = accordion.tabsLabel ?? [];

        const tabExpanders: ExpanderProps[] = accordion.tabsContentCollection.items.map(
          (expanderItem, accordionIndex) => {
            const expander = expanderItem;

            if (expanderItem.__typename === 'ReusableContentSection') {
              return getPropsForReusableContentSectionType(
                expander as ReusableContentSection,
                tabsLabels,
                accordionIndex,
              );
            }

            return getPropsForExpanderType(expander as Expander, tabsLabels, accordionIndex);
          },
        );

        return {
          label: tabs.tabsLabel[index],
          color: tabs.tabColors[index],
          iconType,
          tabExpanders,
        };
      }),
    [tabs.contentCollection.items, tabs.tabsLabel, tabs.tabColors],
  );

  return (
    <div css={containerStyles}>
      <SectionHeading
        heading={tabs.title}
        subtitle={tabs.description}
        containerStyles={{ textAlign: 'left', maxWidth: '940px', margin: 0 }}
      />
      <TabbedAccordion tabs={tabData} />
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.500'),
  maxWidth: '1024px',
  margin: '0 auto',
  width: '100%',
});

const contentSectionDescriptionStyles = css({
  fontFamily: 'Inter, sans-serif',
  color: token('color.text'),
  fontSize: '16px',
  lineHeight: '24px',

  '& p:not(:last-child)': {
    marginBottom: token('space.200'),
  },

  [media.below.md]: {
    '& p': {
      marginBottom: token('space.100'),
    },
  },
});

export default TabsSection;
